import React, { useEffect, useState } from "react"

import * as Sentry from "@sentry/react"
import { Loader2 } from "lucide-react"
import { useLocation, useNavigate } from "react-router-dom"

import { useApplication } from "@/contexts/ApplicationContext"
import { useToast } from "@/hooks/use-toast"
import { getCurrentUser } from "@/services/authService"

const AuthCallback: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const { application, updateApplicationIdAndRefetch } = useApplication()
  const { toast } = useToast()

  useEffect(() => {
    const processAuthentication = async () => {
      setLoading(true)
      const queryParams = new URLSearchParams(location.search)
      const applicationId = queryParams.get("applicationId")

      if (applicationId) {
        try {
          // Update application ID in local storage, refetch and redirect
          await updateApplicationIdAndRefetch(applicationId)
          // Timeout is just for better UX, so that the user can see the loading screen instead of just flashing for a moment
          setTimeout(() => {
            navigate("/application-status")
          }, 1500)

          setLoading(false)
        } catch (error) {
          console.error("Error processing application during auth callback:", error)
          Sentry.captureException(error, {
            extra: {
              context: "AuthCallback.processAuthentication",
              applicationId: applicationId,
              location: location.pathname + location.search,
            },
          })
          toast({
            title: "Error",
            description: "Failed to load your application. Please try again.",
            variant: "destructive",
          })
          setLoading(false)
        }
      } else {
        try {
          // Get the current authenticated user
          const user = await getCurrentUser()

          if (!user) {
            toast({
              title: "Authentication Failed",
              description: "Authentication failed. Please try again.",
              variant: "destructive",
            })
            setError("Authentication failed. Please try again.")
            setLoading(false)
            return
          }

          // Check if we have application data in context
          if (!application.basicDetails.email) {
            toast({
              title: "Missing Information",
              description: "Please complete your basic details first.",
              variant: "destructive",
            })
            // If no application data, redirect to basic details
            navigate("/basic-details")
            return
          }
          // Determine where to redirect based on the current stage
          const redirectPath =
            application.currentStage === "assessment" ? "/assessment" : "/application-status"

          // Show success toast
          toast({
            title: "Application Submitted",
            description: "Your application has been successfully submitted.",
            variant: "default",
          })

          // Redirect to the appropriate page
          navigate(redirectPath)
        } catch (error) {
          console.error("Error in auth callback:", error)
          Sentry.captureException(error, {
            extra: {
              context: "AuthCallback.processAuthentication.noApplicationId",
              hasApplication: !!application?.applicationId,
              currentStage: application?.currentStage,
              location: location.pathname + location.search,
            },
          })
          toast({
            title: "Error",
            description: "Failed to process your application. Please try again.",
            variant: "destructive",
          })
          setError("Failed to process your application. Please try again.")
          setLoading(false)
        }
      }
    }

    processAuthentication()
  }, [application, navigate, toast, location, updateApplicationIdAndRefetch])

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center">
        <Loader2 className="mb-4 h-12 w-12 animate-spin text-honeycomb" />
        <h2 className="mb-2 text-xl font-semibold">Processing your application</h2>
        <p className="text-gray-600">Please wait while we set up your application...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center">
        <div className="w-full max-w-md rounded-md border border-red-200 bg-red-50 px-6 py-4 text-red-700">
          <h2 className="mb-2 text-xl font-semibold">Error</h2>
          <p>{error}</p>
          <button
            onClick={() => navigate("/basic-details")}
            className="mt-4 rounded bg-honeycomb px-4 py-2 text-white hover:bg-amber-500"
          >
            Return to Application
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center">
      <Loader2 className="mb-4 h-12 w-12 animate-spin text-honeycomb" />
      <h2 className="mb-2 text-xl font-semibold">Redirecting...</h2>
    </div>
  )
}

export default AuthCallback
